import type {
  OpenPositionsResponse,
  Position,
  PriceTick,
} from '../types.js';
import { OstiumPriceStream } from './pricing.js';
import { aggregateMarginSummary } from './aggregations.js';
import { maxWithdrawForPosition } from './formatters.js';

type UpdateHandler = (positions: OpenPositionsResponse) => void;
type TickSource = Pick<OstiumPriceStream, 'onTick' | 'onSnapshot' | 'onOpen' | 'onError' | 'onClose' | 'close'>;

function cloneResponse(response: OpenPositionsResponse): OpenPositionsResponse {
  return {
    pairPositions: response.pairPositions.map(pairPos => ({
      position: { ...pairPos.position },
    })),
    marginSummary: { ...response.marginSummary },
    time: response.time,
  };
}

function applyTickToPosition(position: Position, tick: PriceTick): Position {
  const size       = parseFloat(position.szi) || 0;
  const entry      = parseFloat(position.entryPx) || 0;
  const collateral = parseFloat(position.collateralUsed) || 0;
  const maxLeverage = parseFloat(position.maxLeverage) || 0;
  // cumRollover is stored negated (cost convention): add it back to get raw fee impact on PnL.
  const rollover   = parseFloat(position.cumRollover) || 0;
  const isLong     = position.side === 'B';

  const ntl           = size * tick.mid;
  const rawPnl        = isLong ? (tick.mid - entry) * size : (entry - tick.mid) * size;
  const netPnl        = rawPnl + rollover;
  const roe           = collateral > 0 ? netPnl / collateral : 0;
  const currentLeverage = collateral > 0 ? ntl / collateral : 0;
  // liquidationPx is rollover-dependent, not price-dependent — remains valid until the next getOpenPositions() call.
  const maxWithdrawable = maxWithdrawForPosition(collateral, currentLeverage, maxLeverage);

  return {
    ...position,
    ntl:             ntl.toString(),
    unrealizedPnl:   netPnl.toString(),
    returnOnEquity:  roe.toString(),
    maxWithdrawable: maxWithdrawable.toString(),
  };
}

function updateResponseWithTick(
  current: OpenPositionsResponse,
  tick: PriceTick,
  timestampMs: number,
): OpenPositionsResponse {
  if (!tick.pairId) return current;

  let changed = false;
  const pairPositions = current.pairPositions.map(pairPos => {
    if (String(pairPos.position.pairId) !== String(tick.pairId)) {
      return pairPos;
    }
    changed = true;
    return {
      ...pairPos,
      position: applyTickToPosition(pairPos.position, tick),
    };
  });

  if (!changed) return current;

  return {
    pairPositions,
    marginSummary: aggregateMarginSummary(pairPositions),
    time: timestampMs,
  };
}

export class OstiumPositionUpdatesStream {
  private readonly priceStream?: TickSource;
  private readonly updateHandlers: Set<UpdateHandler> = new Set();
  private current: OpenPositionsResponse;
  private readonly trackedPairIds: Set<string>;

  constructor(
    initial: OpenPositionsResponse,
    trackedPairIds: string[],
    priceStream?: TickSource,
  ) {
    this.current = cloneResponse(initial);
    this.priceStream = priceStream;
    this.trackedPairIds = new Set(trackedPairIds);

    if (!priceStream) return;

    priceStream.onSnapshot(ticks => {
      this.ingestSnapshot(ticks);
    });

    priceStream.onTick(tick => {
      this.ingestTick(tick);
    });
  }

  onUpdate(handler: UpdateHandler): () => void {
    this.updateHandlers.add(handler);
    return () => { this.updateHandlers.delete(handler); };
  }

  onOpen(handler: () => void): this {
    this.priceStream?.onOpen(handler);
    return this;
  }

  onError(handler: (event: Error) => void): this {
    this.priceStream?.onError(handler);
    return this;
  }

  onClose(handler: (code: number, reason: string) => void): this {
    this.priceStream?.onClose(handler);
    return this;
  }

  getCurrent(): OpenPositionsResponse {
    return cloneResponse(this.current);
  }

  /**
   * Apply a single externally sourced price tick to the tracked positions.
   *
   * Use this when your app already has its own websocket connection and you want
   * the SDK to handle only the position recalculation logic.
   */
  ingestTick(tick: PriceTick): void {
    if (!tick.pairId || !this.trackedPairIds.has(String(tick.pairId))) return;
    const next = updateResponseWithTick(this.current, tick, tick.timestampSeconds * 1000);
    if (next !== this.current) {
      this.current = next;
      this.emit(next);
    }
  }

  /**
   * Apply a batch of externally sourced ticks, such as a websocket snapshot.
   */
  ingestSnapshot(ticks: PriceTick[]): void {
    let next = this.current;
    for (const tick of ticks) {
      if (!tick.pairId || !this.trackedPairIds.has(String(tick.pairId))) continue;
      next = updateResponseWithTick(next, tick, tick.timestampSeconds * 1000);
    }
    if (next !== this.current) {
      this.current = next;
      this.emit(next);
    }
  }

  close(): void {
    this.priceStream?.close();
  }

  private emit(positions: OpenPositionsResponse): void {
    for (const handler of this.updateHandlers) handler(cloneResponse(positions));
  }
}
